import { z } from 'zod';
/**
 * The closed vocabularies of the requirement model (ADR-006 "Where the configurable/code
 * line falls").
 *
 * Every name here is CODE: adding one is a reviewed deploy, because each needs an
 * evaluator, a presenter and a test. Everything that *uses* these names — which
 * requirement gates which moment, what a rule is called, when it expires — is data.
 *
 * This module sits below every other module in `requirements/` and imports nothing from
 * them, so the readiness engine can share the vocabulary without a cycle.
 */
/**
 * What kind of thing a requirement is. The only place this matters is the per-kind
 * evaluator, which switches exhaustively over it.
 */
export const requirementKindSchema = z.enum([
    /** A signed or uploaded artifact (I-9, policy acknowledgment). */
    'document',
    /** Carries its own issue/expiry dates (a licence, a CPR card). */
    'credential',
    /** Performed and recorded by HR or a third party (background check, reference). */
    'external-check',
    /** N hours or occurrences per rolling window (in-service hours). */
    'hours-accumulator',
    /** Repeats on a cadence (supervisory visit, annual competency). */
    'recurring-event',
]);
/**
 * The per-person status of one requirement. Derived, never stored.
 *
 * There is deliberately NO `waived` (ADR-006 §10): an exception lifts a *gate*, it never
 * satisfies a *requirement*. A `waived` status would let an override silently inflate the
 * compliance percentage HR reports to a surveyor.
 *
 * snake_case matches the shipped `stepStateSchema` — states are snake, keys are kebab.
 */
export const requirementStatusSchema = z.enum([
    'not_applicable',
    'not_started',
    'scheduled',
    'in_progress',
    'awaiting_review',
    'satisfied',
    'expiring_soon',
    'expired',
    'rejected',
    /** Evidence conflicts or dates are impossible — the engine refuses to guess. */
    'indeterminate',
]);
/**
 * Tri-state, and the third state is the point (ADR-006 §9).
 *
 * `unknown` means the trigger input is unanswered — NOT that the answer is no. The naive
 * two-state model reads an unset `drives-on-agency-business` as false and marks an
 * undocumented driver ready for assignment.
 */
export const applicabilitySchema = z.enum(['applies', 'does_not_apply', 'unknown']);
/**
 * A moment that requirements can withhold.
 *
 * Phase 1 ships exactly ONE (ADR-006 §3). `hire`, `independent-patient-care` and
 * `contract-service-start` are anticipated but deliberately absent: each needs a screen, a
 * banner and a report column, so adding one is a deploy regardless of how configurable
 * gate membership is. Seeding a gate with no UI behind it would produce a readiness state
 * nobody can see or act on.
 */
export const gateSchema = z.enum(['first-assignment', 'employment', 'independent-service', 'driving']);
export const gateLabels = {
    'first-assignment': 'Overall assignment checks', employment: 'Employment',
    'independent-service': 'Independent care / services', driving: 'Driving',
};
/** Where a requirement is allowed to appear. Independent of whether it blocks anything. */
export const surfaceSchema = z.enum([
    'person-checklist',
    'person-portal',
    'hr-checklist',
    'dashboard-rollup',
    'reports',
    'audit-export',
]);
/**
 * The closed set of per-person facts a trigger may read.
 *
 * Closed on purpose: an open string here is an expression language with extra steps, and
 * a renamed attribute would break rules silently, in production, on one person's record.
 * A value is either present or absent — absent means UNANSWERED, which is what makes
 * `Applicability.unknown` reachable.
 */
export const personAttributeSchema = z.enum([
    'occupational-blood-exposure',
    'transports-patients',
    'requires-respirator',
    'provides-patient-services',
    // NOTE: there is deliberately no 'drives-on-agency-business'. Driving is a person TAG
    // (decision 3), and the tag is what puts the driving rows in scope. Having both meant
    // the tag added the rows and the unanswered attribute immediately hid them again, so a
    // person tagged as a driver saw nothing. One fact, one representation.
    'qualified-via-aide-registry',
    'has-conviction-disclosure',
    'had-exposure-event',
    'requires-fitness-for-duty',
    'holds-professional-licence',
    'provides-direct-care',
    /**
     * Added when the catalog was seeded (#36). Both are genuine per-person facts with no
     * proxy in the list above, and without them two rows sit permanently open on every
     * RN's checklist — the exact failure decision 7 exists to prevent.
     *
     * Adding a name here is a reviewed deploy, by design. That is the cost of a closed
     * vocabulary, and it is the cost worth paying: an open one is an expression language,
     * and a renamed field would then break rules silently, in production, on one record.
     */
    /** Catalog Q14 — only RNs designated as aide trainers carry the trainer qualification. */
    'designated-aide-trainer',
    /** Catalog Q44 — the EVV acknowledgment applies to Medicaid service delivery only. */
    'delivers-medicaid-services',
]);
/** Lifecycle phase a requirement belongs to. */
export const requirementPhaseSchema = z.enum(['onboarding', 'continuous', 'both']);
/** Who the requirement hangs off — the person, or the agency (compliance catalog §5). */
export const requirementOwnerSchema = z.enum(['person', 'agency']);
/**
 * A jurisdiction the agency operates in, as a US state code.
 *
 * Decision 2 is "Virginia only", and the way to honour that is a LIST rather than an
 * assumption: adding a second state must be data entry, not a code change. Nothing in the
 * codebase may hardcode 'VA'.
 *
 * A requirement carries the jurisdictions it belongs to. An EMPTY list means "everywhere"
 * — the common case, since most rows are federal or accreditor-driven. A non-empty list
 * means the row is only in scope where the agency actually operates, which is why the
 * filter runs in the scope pass rather than as a trigger: a licence for a state we do not
 * work in was never this person's requirement, which is a different statement from
 * "triggered but not yet applicable".
 */
export const jurisdictionSchema = z.string().regex(/^[A-Z]{2}$/);
/**
 * Agency-level configuration. One document, and the only place the operating states live.
 */
export const agencySettingsSchema = z.object({
    /**
     * IANA zone the agency's business day runs on. Required, so a new jurisdiction has to state
     * it rather than inheriting Virginia's by silence — the same reason nothing may hardcode 'VA'.
     */
    timeZone: z.string().min(1),
    id: z.literal('agency-settings'),
    docType: z.literal('agency-settings'),
    jurisdictions: z.array(jurisdictionSchema).min(1),
    /** Decision 1. Recorded so a rule can say which accreditor it derives from. */
    accreditors: z.array(z.enum(['CHAP', 'ACHC', 'ACHC-legacy'])).min(1),
});
/** True when a requirement's jurisdictions overlap the agency's, or it applies everywhere. */
export function inJurisdiction(requirementJurisdictions, agencyJurisdictions) {
    if (requirementJurisdictions.length === 0)
        return true;
    return requirementJurisdictions.some((j) => agencyJurisdictions.includes(j));
}
/**
 * Exhaustiveness guard for switch dispatch, matching the `effects.ts` pattern. Reaching
 * it is a type error at compile time and a loud throw if data smuggles an unknown value
 * past a schema.
 */
export function assertNever(value, context) {
    throw new Error(`${context}: unhandled variant ${JSON.stringify(value)}`);
}
//# sourceMappingURL=vocabulary.js.map